import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, RefreshCw, Check, X, Edit2, Home, Settings, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';

// Get from environment as variables (build-time)
const ADMIN_USER = process.env.REACT_APP_KIOSK_ADMIN_USER;
const ADMIN_PASS = process.env.REACT_APP_KIOSK_ADMIN_PASS;

const KioskMachineManager = () => {
    const navigate = useNavigate();
    const [machines, setMachines] = useState([]);
    const [groupedMachines, setGroupedMachines] = useState({});
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeSite, setActiveSite] = useState(null);
    const [addingToSite, setAddingToSite] = useState(null);
    const [newMachineNo, setNewMachineNo] = useState('');
    // Admin login states
    const [authUser, setAuthUser] = useState('');
    const [authPass, setAuthPass] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // // Require login before loading anything
    // useEffect(() => {
    //     const saved = localStorage.getItem('kiosk_admin_logged');
    //     if (saved === 'yes') setIsAuthenticated(true);
    // }, []);

    // Group machines by site code
    useEffect(() => {
        const grouped = machines.reduce((acc, machine) => {
            if (!acc[machine.siteCode]) {
                acc[machine.siteCode] = {
                    siteCode: machine.siteCode,
                    siteId: machine.siteId,
                    hospitalName: machine.hospitalName,
                    location: machine.location,
                    machines: []
                };
            }
            acc[machine.siteCode].machines.push(machine);
            return acc;
        }, {});
        setGroupedMachines(grouped);
    }, [machines]);

    // Check if site is already set in localStorage
    useEffect(() => {
        const storedSiteCode = localStorage.getItem('site_code');
        const storedKioskMachineNo = localStorage.getItem('kiosk_machine_no');

        if (storedSiteCode && storedKioskMachineNo) {
            setActiveSite({ siteCode: storedSiteCode, kioskMachineNo: storedKioskMachineNo });
        }
    }, []);

    // Set this site as active kiosk
    const handleSetThisSite = (machine) => {
        if (!machine.kioskMachineNo) {
            setError('Please assign a kiosk machine number first');
            setTimeout(() => setError(null), 3000);
            return;
        }

        localStorage.setItem('site_code', machine.siteCode);
        localStorage.setItem('kiosk_machine_no', machine.kioskMachineNo);
        localStorage.setItem('site_id', machine.siteId);
        setActiveSite({ siteCode: machine.siteCode, kioskMachineNo: machine.kioskMachineNo });
        setSuccess(`Site "${machine.siteCode}" with Machine "${machine.kioskMachineNo}" set successfully!`);
        setTimeout(() => setSuccess(null), 3000);
    };

    // Fetch all kiosk machines
    const fetchMachines = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1');
            const response = await axiosInstance.get('/api/kiosk-machine', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setMachines(response.data.data);
            setError(null);
        } catch (error) {
            console.error('Error fetching machines:', error);
            setError('Failed to load kiosk machines');
        } finally {
            setLoading(false);
        }
    };

    // Sync data from HIMS API
    const syncFromHIMS = async () => {
        try {
            setSyncing(true);
            setError(null);
            const token = localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1');
            const response = await axiosInstance.post(
                '/api/kiosk-machine/sync',
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setSuccess(`Synced ${response.data.totalRecords} records successfully`);
            await fetchMachines();
            setTimeout(() => setSuccess(null), 3000);
        } catch (error) {
            console.error('Error syncing:', error);
            setError('Failed to sync data from HIMS');
        } finally {
            setSyncing(false);
        }
    };

    // Update kiosk machine number
    const updateMachineNo = async (id, kioskMachineNo) => {
        try {
            setSaving(true);
            const token = localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1');
            await axiosInstance.put(
                `/api/kiosk-machine/${id}`,
                { kioskMachineNo },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setSuccess('Kiosk machine number updated successfully');
            await fetchMachines();
            setEditingId(null);
            setEditValue('');
            setTimeout(() => setSuccess(null), 3000);
        } catch (error) {
            console.error('Error updating:', error);
            setError('Failed to update kiosk machine number');
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        fetchMachines();
    }, []);

    // Prompt if not authenticated
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <form
                    onSubmit={e => {
                        e.preventDefault();
                        if (
                            authUser === ADMIN_USER &&
                            authPass === ADMIN_PASS
                        ) {
                            setIsAuthenticated(true);
                            // localStorage.setItem('kiosk_admin_logged', 'yes');
                        } else {
                            alert('Invalid username/password');
                        }
                    }}
                    className="bg-white p-10 rounded-xl shadow-xl flex flex-col w-full max-w-xs"
                    autoComplete="off"
                >
                    <h2 className="mb-6 text-center font-bold text-lg text-[#0FA968]">Kiosk Admin Login</h2>
                    <label className="mb-1 text-gray-700 text-sm font-medium">Username</label>
                    <input
                        className="mb-5 px-3 py-2 border rounded shadow-inner outline-none focus:ring-2 ring-[#0FA968]"
                        type="text"
                        value={authUser}
                        onChange={e => setAuthUser(e.target.value)}
                        autoFocus
                        required
                        autoComplete="new-username"
                    />
                    <label className="mb-1 text-gray-700 text-sm font-medium">Password</label>
                    <input
                        className="mb-6 px-3 py-2 border rounded shadow-inner outline-none focus:ring-2 ring-[#0FA968]"
                        type="password"
                        value={authPass}
                        onChange={e => setAuthPass(e.target.value)}
                        required
                        autoComplete="new-password"
                    />
                    <button
                        type="submit"
                        className="bg-[#0FA968] text-white font-bold px-4 py-2 rounded hover:bg-[#138F5E] transition"
                    >
                        Login
                    </button>
                </form>
            </div>
        );
    }

    const handleEdit = (machine) => {
        setEditingId(machine.id);
        setEditValue(machine.kioskMachineNo || '');
    };

    const handleSave = (id) => {
        updateMachineNo(id, editValue);
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditValue('');
    };

    const handleAddMachine = async (siteCode) => {
        if (!newMachineNo.trim()) {
            setError('Please enter a machine number');
            setTimeout(() => setError(null), 3000);
            return;
        }

        try {
            setSaving(true);
            const token = localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1');
            const siteInfo = groupedMachines[siteCode];

            await axiosInstance.post(
                '/api/kiosk-machine',
                {
                    siteCode: siteInfo.siteCode,
                    siteId: siteInfo.siteId,
                    location: siteInfo.location,
                    hospitalName: siteInfo.hospitalName,
                    kioskMachineNo: newMachineNo.trim(),
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSuccess('Kiosk machine added successfully');
            await fetchMachines();
            setAddingToSite(null);
            setNewMachineNo('');
            setTimeout(() => setSuccess(null), 3000);
        } catch (error) {
            console.error('Error adding machine:', error);
            setError('Failed to add kiosk machine');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteMachine = async (id, machineName) => {
        if (!window.confirm(`Are you sure you want to delete kiosk machine "${machineName}"?`)) {
            return;
        }

        try {
            const token = localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1');
            await axiosInstance.delete(`/api/kiosk-machine/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setSuccess('Kiosk machine deleted successfully');
            await fetchMachines();
            setTimeout(() => setSuccess(null), 3000);
        } catch (error) {
            console.error('Error deleting machine:', error);
            setError('Failed to delete kiosk machine');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0FA968]"></div>
                    <p className="mt-4 text-gray-600">Loading kiosk machines...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Kiosk Machine Master</h1>
                            <p className="text-gray-600 mt-2">Manage kiosk machines and assign machine numbers</p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate('/')}
                                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition duration-200"
                            >
                                <Home className="w-5 h-5" />
                                Home
                            </button>
                            <button
                                onClick={syncFromHIMS}
                                disabled={syncing}
                                className="flex items-center gap-2 px-6 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                                {syncing ? 'Syncing...' : 'Sync from HIMS'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Alerts */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Check className="w-5 h-5" />
                            <span>{success}</span>
                        </div>
                        <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Site Code
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Hospital Name / Location
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Kiosk Machines
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {Object.keys(groupedMachines).map((siteCode) => {
                                    const site = groupedMachines[siteCode];
                                    return (
                                        <tr key={siteCode} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                                                    {site.siteCode}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-semibold text-gray-900">{site.hospitalName}</div>
                                                <div className="text-xs text-gray-500">{site.location}</div>
                                                <div className="text-xs text-gray-400">Site ID: {site.siteId}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="space-y-2">
                                                    {site.machines.map((machine) => (
                                                        <div key={machine.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                                                            {editingId === machine.id ? (
                                                                <input
                                                                    type="text"
                                                                    value={editValue}
                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                    className="flex-1 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-[#0FA968] focus:border-transparent text-sm"
                                                                    placeholder="Enter machine number"
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <span className={`flex-1 text-sm ${machine.kioskMachineNo ? 'text-gray-900 font-semibold' : 'text-gray-400 italic'}`}>
                                                                    {machine.kioskMachineNo || 'Not assigned'}
                                                                </span>
                                                            )}

                                                            <div className="flex gap-1">
                                                                {editingId === machine.id ? (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleSave(machine.id)}
                                                                            disabled={saving}
                                                                            className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200 disabled:opacity-50"
                                                                            title="Save"
                                                                        >
                                                                            <Check className="w-3 h-3" />
                                                                        </button>
                                                                        <button
                                                                            onClick={handleCancel}
                                                                            disabled={saving}
                                                                            className="p-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200 disabled:opacity-50"
                                                                            title="Cancel"
                                                                        >
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <button
                                                                            onClick={() => handleEdit(machine)}
                                                                            className="p-1 bg-[#0FA968] text-white rounded hover:bg-[#138F5E] transition duration-200"
                                                                            title="Edit"
                                                                        >
                                                                            <Edit2 className="w-3 h-3" />
                                                                        </button>
                                                                        {machine.kioskMachineNo ? (
                                                                            activeSite?.siteCode === machine.siteCode && activeSite?.kioskMachineNo === machine.kioskMachineNo ? (
                                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-500">
                                                                                    <Check className="w-3 h-3 mr-1" />
                                                                                    Active
                                                                                </span>
                                                                            ) : (
                                                                                <button
                                                                                    onClick={() => handleSetThisSite(machine)}
                                                                                    className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-200"
                                                                                    title="Set as active"
                                                                                >
                                                                                    <Settings className="w-3 h-3" />
                                                                                </button>
                                                                            )
                                                                        ) : null}
                                                                        {site.machines.length > 1 && (
                                                                            <button
                                                                                onClick={() => handleDeleteMachine(machine.id, machine.kioskMachineNo)}
                                                                                className="p-1 bg-red-600 text-white rounded hover:bg-red-700 transition duration-200"
                                                                                title="Delete"
                                                                            >
                                                                                <Trash2 className="w-3 h-3" />
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}

                                                    {addingToSite === siteCode ? (
                                                        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border-2 border-blue-300">
                                                            <input
                                                                type="text"
                                                                value={newMachineNo}
                                                                onChange={(e) => setNewMachineNo(e.target.value)}
                                                                className="flex-1 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-[#0FA968] focus:border-transparent text-sm"
                                                                placeholder="Enter machine number (e.g., kiosk1)"
                                                                autoFocus
                                                            />
                                                            <button
                                                                onClick={() => handleAddMachine(siteCode)}
                                                                disabled={saving}
                                                                className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200 disabled:opacity-50"
                                                                title="Save"
                                                            >
                                                                <Check className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setAddingToSite(null);
                                                                    setNewMachineNo('');
                                                                }}
                                                                disabled={saving}
                                                                className="p-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200 disabled:opacity-50"
                                                                title="Cancel"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() => setAddingToSite(siteCode)}
                                                    disabled={addingToSite === siteCode}
                                                    className="flex items-center gap-2 px-3 py-2 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200 disabled:opacity-50"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                    Add Machine
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {Object.keys(groupedMachines).length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-lg">No kiosk machines found</p>
                            <p className="text-gray-400 mt-2">Click "Sync from HIMS" to load data</p>
                        </div>
                    )}
                </div>

                {/* Footer Info */}
                <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <div>
                            Total Sites: <span className="font-semibold text-gray-900">{Object.keys(groupedMachines).length}</span>
                        </div>
                        <div>
                            Total Machines: <span className="font-semibold text-gray-900">{machines.length}</span>
                        </div>
                        <div>
                            Assigned Machines: <span className="font-semibold text-gray-900">
                                {machines.filter(m => m.kioskMachineNo).length}
                            </span>
                        </div>
                        <div>
                            Unassigned: <span className="font-semibold text-gray-900">
                                {machines.filter(m => !m.kioskMachineNo).length}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KioskMachineManager;
